import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import {
  Search,
  Person,
  CheckCircle,
  Error,
  Refresh,
  PlayArrow,
  Info,
  Email,
  PersonAdd,
  NavigateBefore,
  NavigateNext,
  ListAlt,
  ExpandMore,
  Warning,
  Clear,
  Cached,
} from '@mui/icons-material';
import { eligibilityCacheService, UserWithCache, PaginationInfo, SummaryInfo, UsersWithCacheResult, ClearCacheResult, UserDetailsResult } from '../../services/eligibilityCacheService';
import { getFunctions, httpsCallable } from 'firebase/functions';

const EligibilityCacheManager: React.FC = () => {
  const [usersWithCache, setUsersWithCache] = useState<UserWithCache[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [summary, setSummary] = useState<SummaryInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Selected user for operations
  const [selectedUser, setSelectedUser] = useState<UserWithCache | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [operationType, setOperationType] = useState<'clear' | 'recalculate' | 'compare' | null>(null);
  
  // Debug state
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isDebugLoading, setIsDebugLoading] = useState(false);
  
  // User search state
  const [searchUserId, setSearchUserId] = useState<string>('');
  const [searchedUser, setSearchedUser] = useState<UserDetailsResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchedUserDialog, setShowSearchedUserDialog] = useState(false);

  /**
   * Debug eligibility cache data
   */
  const debugEligibilityCache = async () => {
    setIsDebugLoading(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const debugCache = httpsCallable(functions, 'adminDebugEligibilityCache');
      const result = await debugCache();
      
      setDebugInfo(result.data);
      console.log('Debug cache result:', result.data);
    } catch (err: unknown) {
      console.error('Error debugging cache:', err);
      const errorMessage = err instanceof Error ? (err as Error).message : 'Unknown error';
      setError(`Failed to debug cache: ${errorMessage}`);
    } finally {
      setIsDebugLoading(false);
    }
  };

  /**
   * Load users with eligibility cache
   */
  const loadUsersWithCache = async (page: number = 1, pageLimit: number = 20) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await eligibilityCacheService.getUsersWithCache(page, pageLimit);
      
      setUsersWithCache(result.users);
      setPagination(result.pagination);
      setSummary(result.summary);
      setCurrentPage(page);
      setLimit(pageLimit);
    } catch (err: unknown) {
      console.error('Error loading users with cache:', err);
      let errorMessage = 'Unknown error';
      if (err && typeof err === 'object') {
        if ('message' in err && typeof err.message === 'string') {
          errorMessage = err.message;
        } else if ('code' in err && typeof err.code === 'string') {
          errorMessage = `Firebase error: ${err.code}`;
        }
      }
      setError(`Failed to load users: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clear cache and recalculate for a specific user
   */
  const handleClearCacheAndRecalculate = async (user: UserWithCache) => {
    setIsProcessing(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await eligibilityCacheService.clearCacheAndRecalculate(user.uid);
      
      if (result.success) {
        setSuccess(`Successfully cleared cache and recalculated for ${user.email}`);
        // Reload the current page to reflect changes
        await loadUsersWithCache(currentPage, limit);
      } else {
        setError(result.message || 'Failed to clear cache and recalculate');
      }
    } catch (err: unknown) {
      console.error('Error clearing cache and recalculating:', err);
      let errorMessage = 'Unknown error';
      if (err && typeof err === 'object') {
        if ('message' in err && typeof err.message === 'string') {
          errorMessage = err.message;
        } else if ('code' in err && typeof err.code === 'string') {
          errorMessage = `Firebase error: ${err.code}`;
        }
      }
      setError(`Failed to clear cache and recalculate: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
      setShowConfirmDialog(false);
      setSelectedUser(null);
      setOperationType(null);
    }
  };

  /**
   * Get user details for inspection
   */
  const handleGetUserDetails = async (user: UserWithCache) => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await eligibilityCacheService.getUserDetails(user.uid);
      
      if (result.success) {
        setSelectedUser(user);
        setShowDetailsDialog(true);
      } else {
        setError('Failed to get user details');
      }
    } catch (err: unknown) {
      console.error('Error getting user details:', err);
      const errorMessage = err instanceof Error ? (err as Error).message : 'Unknown error';
      setError(`Failed to get user details: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Search for a user by UserID
   */
  const handleSearchUser = async () => {
    if (!searchUserId.trim()) {
      setError('Please enter a UserID');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSearchedUser(null);

    try {
      const result = await eligibilityCacheService.getUserDetails(searchUserId.trim());
      
      if (result.success) {
        setSearchedUser(result);
        setShowSearchedUserDialog(true);
      } else {
        setError('User not found or failed to retrieve user details');
      }
    } catch (err: unknown) {
      console.error('Error searching for user:', err);
      const errorMessage = err instanceof Error ? (err as Error).message : 'Unknown error';
      setError(`Failed to search for user: ${errorMessage}`);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Clear cache for searched user
   */
  const handleClearCacheForSearchedUser = async () => {
    if (!searchedUser || !searchedUser.userInfo?.uid) {
      setError('No user selected');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await eligibilityCacheService.clearCacheAndRecalculate(searchedUser.userInfo.uid);
      
      if (result.success) {
        setSuccess(`Successfully cleared cache and recalculated for ${searchedUser.userInfo.email}`);
        // Reload searched user data
        await handleSearchUser();
        // Also reload the list if it's loaded
        if (usersWithCache.length > 0) {
          await loadUsersWithCache(currentPage, limit);
        }
      } else {
        setError(result.message || 'Failed to clear cache and recalculate');
      }
    } catch (err: unknown) {
      console.error('Error clearing cache and recalculating:', err);
      const errorMessage = err instanceof Error ? (err as Error).message : 'Unknown error';
      setError(`Failed to clear cache and recalculate: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage: number) => {
    loadUsersWithCache(newPage, limit);
  };

  /**
   * Handle limit change
   */
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    loadUsersWithCache(1, newLimit);
  };

  /**
   * Handle operation confirmation
   */
  const handleConfirmOperation = () => {
    if (!selectedUser || !operationType) return;
    
    switch (operationType) {
      case 'clear':
        handleClearCacheAndRecalculate(selectedUser);
        break;
      default:
        break;
    }
  };

  /**
   * Get status chip for user
   */
  const getStatusChip = (user: UserWithCache) => {
    if (user.needsRefresh) {
      return (
        <Chip
          label="Needs Refresh"
          color="warning"
          size="small"
          icon={<Refresh />}
        />
      );
    } else {
      return (
        <Chip
          label="Up to Date"
          color="success"
          size="small"
          icon={<CheckCircle />}
        />
      );
    }
  };

  /**
   * Get grade difference chip
   */
  const getGradeDifferenceChip = (user: UserWithCache) => {
    if (user.gradeDifference) {
      return (
        <Chip
          label={`Profile: ${user.profileGrade} → Derived: ${user.derivedGrade}`}
          color="warning"
          size="small"
        />
      );
    } else {
      return (
        <Chip
          label={`Grade: ${user.derivedGrade}`}
          color="success"
          size="small"
        />
      );
    }
  };

  // Load users on component mount
  useEffect(() => {
    loadUsersWithCache();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Eligibility Cache Management
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage users with existing Firebase eligibility cache. Clear cache and recalculate with new grade derivation logic.
      </Typography>

      {/* Summary Card */}
      {summary && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cache Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Users with Cache
                </Typography>
                <Typography variant="h4" color="primary">
                  {summary.totalUsersWithCache}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Need Refresh
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {summary.needsRefreshCount}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Processed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {summary.processedCount}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* User Search Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search User by UserID
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for any user by their UserID to view profile data and clear cache/recalculate eligibility.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="UserID"
              value={searchUserId}
              onChange={(e) => setSearchUserId(e.target.value)}
              placeholder="Enter UserID"
              size="small"
              sx={{ flexGrow: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchUser();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearchUser}
              disabled={isSearching || !searchUserId.trim()}
              startIcon={isSearching ? <CircularProgress size={20} /> : <Search />}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              onClick={() => loadUsersWithCache(currentPage, limit)}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <Refresh />}
            >
              {isLoading ? 'Loading...' : 'Refresh List'}
            </Button>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select
                value={limit}
                label="Per Page"
                onChange={(e) => handleLimitChange(Number(e.target.value))}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              onClick={debugEligibilityCache}
              disabled={isDebugLoading}
              startIcon={isDebugLoading ? <CircularProgress size={20} /> : <Info />}
            >
              {isDebugLoading ? 'Debugging...' : 'Debug Cache'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Debug Information */}
      {debugInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Debug Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Metadata Users
                </Typography>
                <Typography variant="h4" color="primary">
                  {debugInfo.metadata?.userCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User IDs: {debugInfo.metadata?.userIds?.join(', ') || 'None'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Cache Users
                </Typography>
                <Typography variant="h4" color="secondary">
                  {debugInfo.cache?.userCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User IDs: {debugInfo.cache?.userIds?.join(', ') || 'None'}
                </Typography>
              </Grid>
            </Grid>
            {debugInfo.metadata?.sampleMetadata && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Sample Metadata</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(debugInfo.metadata.sampleMetadata, null, 2)}
                  </pre>
                </AccordionDetails>
              </Accordion>
            )}
            {debugInfo.cache?.sampleCache && (
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Sample Cache</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(debugInfo.cache.sampleCache, null, 2)}
                  </pre>
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Users with Eligibility Cache
          </Typography>
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : usersWithCache.length === 0 ? (
            <Alert severity="info">
              No users with eligibility cache found.
            </Alert>
          ) : (
            <>
              <List>
                {usersWithCache.map((user) => (
                  <ListItem key={user.uid} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {user.displayName || user.email}
                          </Typography>
                          {getStatusChip(user)}
                          {getGradeDifferenceChip(user)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Courses: {user.courseCount} | Programs: {user.programCount} | 
                            Last Sign In: {new Date(user.lastSignIn).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            onClick={() => handleGetUserDetails(user)}
                            disabled={isProcessing}
                          >
                            <Info />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Clear Cache & Recalculate">
                          <IconButton
                            onClick={() => {
                              setSelectedUser(user);
                              setOperationType('clear');
                              setShowConfirmDialog(true);
                            }}
                            disabled={isProcessing}
                            color={user.needsRefresh ? "warning" : "default"}
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              {/* Pagination */}
              {pagination && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Page {pagination.currentPage} of {pagination.totalPages} 
                    ({pagination.totalUsers} total users)
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<NavigateBefore />}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPreviousPage || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      endIcon={<NavigateNext />}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage || isLoading}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={showDetailsDialog} onClose={() => setShowDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          User Details: {selectedUser?.displayName || selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Typography variant="h6" gutterBottom>Profile Information</Typography>
              <Typography variant="body2">Email: {selectedUser.email}</Typography>
              <Typography variant="body2">Display Name: {selectedUser.displayName}</Typography>
              <Typography variant="body2">Created: {new Date(selectedUser.createdAt).toLocaleString()}</Typography>
              <Typography variant="body2">Last Sign In: {new Date(selectedUser.lastSignIn).toLocaleString()}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Grade Information</Typography>
              <Typography variant="body2">Profile Grade: {selectedUser.profileGrade || 'Not set'}</Typography>
              <Typography variant="body2">Derived Grade: {selectedUser.derivedGrade}</Typography>
              <Typography variant="body2">Grade Difference: {selectedUser.gradeDifference ? 'Yes' : 'No'}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Course Information</Typography>
              <Typography variant="body2">Course Count: {selectedUser.courseCount}</Typography>
              <Typography variant="body2">Has Grade 12 Courses: {selectedUser.hasGrade12Courses ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2">Has Grade 11 Courses: {selectedUser.hasGrade11Courses ? 'Yes' : 'No'}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Cache Information</Typography>
              <Typography variant="body2">Program Count: {selectedUser.programCount}</Typography>
              <Typography variant="body2">Cache Keys: {selectedUser.cacheKeys.join(', ')}</Typography>
              <Typography variant="body2">Needs Refresh: {selectedUser.needsRefresh ? 'Yes' : 'No'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>
          Confirm {operationType === 'clear' ? 'Clear Cache & Recalculate' : 'Operation'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {operationType === 'clear' ? 'clear the eligibility cache and recalculate' : 'perform this operation'} for{' '}
            <strong>{selectedUser?.displayName || selectedUser?.email}</strong>?
          </Typography>
          {operationType === 'clear' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will clear the existing eligibility cache and recalculate with the new grade derivation logic.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmOperation}
            color="warning"
            variant="contained"
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Searched User Dialog */}
      <Dialog open={showSearchedUserDialog} onClose={() => setShowSearchedUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          User Details: {searchedUser?.userInfo?.displayName || searchedUser?.userInfo?.email || searchUserId}
        </DialogTitle>
        <DialogContent>
          {searchedUser && (
            <Box>
              <Typography variant="h6" gutterBottom>Profile Information</Typography>
              <Typography variant="body2">UserID: {searchedUser.userInfo?.uid}</Typography>
              <Typography variant="body2">Email: {searchedUser.userInfo?.email}</Typography>
              <Typography variant="body2">Display Name: {searchedUser.userInfo?.displayName || 'Not set'}</Typography>
              <Typography variant="body2">Created: {searchedUser.userInfo?.createdAt ? new Date(searchedUser.userInfo.createdAt).toLocaleString() : 'Unknown'}</Typography>
              <Typography variant="body2">Last Sign In: {searchedUser.userInfo?.lastSignIn ? new Date(searchedUser.userInfo.lastSignIn).toLocaleString() : 'Unknown'}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Grade Information</Typography>
              <Typography variant="body2">Profile Grade: {searchedUser.profileData?.profileGrade || 'Not set'}</Typography>
              <Typography variant="body2">Derived Grade: {searchedUser.profileData?.derivedGrade || 'Not available'}</Typography>
              <Typography variant="body2">Grade Difference: {searchedUser.profileData?.gradeDifference ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2">Has Profile Data: {searchedUser.profileData?.hasProfileData ? 'Yes' : 'No'}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Course Information</Typography>
              <Typography variant="body2">Course Count: {searchedUser.courseData?.courseCount || 0}</Typography>
              <Typography variant="body2">Has Grade 12 Courses: {searchedUser.courseData?.hasGrade12Courses ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2">Has Grade 11 Courses: {searchedUser.courseData?.hasGrade11Courses ? 'Yes' : 'No'}</Typography>
              {searchedUser.courseData?.courses && searchedUser.courseData.courses.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Courses:</Typography>
                  <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                    {searchedUser.courseData.courses.join(', ')}
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Eligibility Cache Information</Typography>
              <Typography variant="body2">Has Cache: {searchedUser.eligibilityCache?.hasCache ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2">Program Count: {searchedUser.eligibilityCache?.programCount || 0}</Typography>
              {searchedUser.eligibilityCache?.cacheKeys && searchedUser.eligibilityCache.cacheKeys.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Cache Keys (first 10):</Typography>
                  <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                    {searchedUser.eligibilityCache.cacheKeys.slice(0, 10).join(', ')}
                    {searchedUser.eligibilityCache.cacheKeys.length > 10 && ` ... (${searchedUser.eligibilityCache.cacheKeys.length - 10} more)`}
                  </Typography>
                </Box>
              )}
              {searchedUser.eligibilityCache?.error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {searchedUser.eligibilityCache.error}
                </Alert>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>Data Status</Typography>
              <Typography variant="body2">Has Data: {searchedUser.dataStatus?.hasData ? 'Yes' : 'No'}</Typography>
              {searchedUser.dataStatus?.reasons && searchedUser.dataStatus.reasons.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Status Details:</Typography>
                  <List dense>
                    {searchedUser.dataStatus.reasons.map((reason: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0 }}>
                        <ListItemText primary={reason} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSearchedUserDialog(false)}>Close</Button>
          <Button
            onClick={() => {
              setShowSearchedUserDialog(false);
              setShowConfirmDialog(true);
              setOperationType('clear');
              // Create a temporary user object for the confirmation
              if (searchedUser?.userInfo) {
                setSelectedUser({
                  uid: searchedUser.userInfo.uid,
                  email: searchedUser.userInfo.email,
                  displayName: searchedUser.userInfo.displayName || '',
                  createdAt: searchedUser.userInfo.createdAt || '',
                  lastSignIn: searchedUser.userInfo.lastSignIn || '',
                  migratedAt: null, // Not available in UserDetailsResult
                  profileGrade: searchedUser.profileData?.profileGrade || '',
                  derivedGrade: searchedUser.profileData?.derivedGrade || '',
                  gradeDifference: searchedUser.profileData?.gradeDifference || false,
                  courseCount: searchedUser.courseData?.courseCount || 0,
                  hasGrade12Courses: searchedUser.courseData?.hasGrade12Courses || false,
                  hasGrade11Courses: searchedUser.courseData?.hasGrade11Courses || false,
                  programCount: searchedUser.eligibilityCache?.programCount || 0,
                  cacheKeys: searchedUser.eligibilityCache?.cacheKeys || [],
                  needsRefresh: searchedUser.profileData?.gradeDifference || false,
                });
              }
            }}
            variant="contained"
            color="warning"
            startIcon={<Clear />}
          >
            Clear Cache & Recalculate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EligibilityCacheManager;
